import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type {
  IAdminNotificationRepository,
  IAbandonedCartRepository,
  AdminNotification,
  CreateNotificationInput,
  NotificationFilters,
  NotificationKind,
  NotificationSummary,
  AbandonedCart,
  AbandonedCartItem,
  AbandonedCartStatus,
  UpsertAbandonedCartInput,
  UpdateAbandonedCartInput,
  AbandonedCartFilters,
} from '../../../interfaces/src';
import { ensureLowStockNotificationsForArticles } from './low-stock-notifications';

const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

// ============================================================
// ADMIN NOTIFICATIONS
// ============================================================

export class SupabaseAdminNotificationRepository implements IAdminNotificationRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: NotificationFilters & { limit?: number }): Promise<AdminNotification[]> {
    let q = anyFrom(this.supabase)('admin_notifications').select('*');
    if (filters?.kind) q = q.eq('kind', filters.kind);
    if (filters?.kinds && filters.kinds.length > 0) q = q.in('kind', filters.kinds);
    if (filters?.isRead !== undefined) q = q.eq('is_read', filters.isRead);
    q = q.order('created_at', { ascending: false }).limit(filters?.limit ?? 100);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(map);
  }

  async getUnreadCount(filters?: Pick<NotificationFilters, 'kind' | 'kinds'>): Promise<number> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('admin_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);
    if (filters?.kind) q = q.eq('kind', filters.kind);
    if (filters?.kinds && filters.kinds.length > 0) q = q.in('kind', filters.kinds);
    const { count, error } = await q;
    if (error) throw error;
    return count ?? 0;
  }

  /**
   * Compteurs + dernières notifications de chaque type, en UNE requête.
   *
   * Remplace les cinq pollers de la coque (barre latérale + deux cloches ×
   * compteur/liste), qui repartaient toutes les 180 s en permanence. La RPC est
   * `SECURITY INVOKER` : scopée par les mêmes policies RLS que la table.
   */
  async summary(perKind = 15): Promise<NotificationSummary> {
    const { data, error } = await (this.supabase as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    }).rpc('notification_summary', { p_per_kind: perKind });
    if (error) throw error;
    const payload = (data ?? {}) as {
      unreadByKind?: Record<string, number>;
      latest?: Record<string, unknown>[];
    };
    return {
      unreadByKind: (payload.unreadByKind ?? {}) as NotificationSummary['unreadByKind'],
      // La RPC rend déjà les clés du domaine ; on repasse par `map` pour que
      // les valeurs par défaut (metadata {}, isRead false) restent au MÊME
      // endroit que pour `list()` — deux mappages divergents finiraient par
      // afficher deux choses différentes pour la même notification.
      latest: (payload.latest ?? []).map((row) => ({
        ...map(toRowShape(row)),
      })),
    };
  }

  async create(input: CreateNotificationInput): Promise<AdminNotification> {
    const { data, error } = await anyFrom(this.supabase)('admin_notifications')
      .insert({
        kind: input.kind,
        title: input.title,
        message: input.message ?? null,
        link_path: input.linkPath ?? null,
        entity_type: input.entityType ?? null,
        entity_id: input.entityId ?? null,
        metadata: input.metadata ?? {},
      })
      .select().single();
    if (error) throw error;
    return map(data);
  }

  async markAsRead(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('admin_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }

  async markAllAsRead(filters?: Pick<NotificationFilters, 'kinds'>): Promise<void> {
    let q = anyFrom(this.supabase)('admin_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('is_read', false);
    if (filters?.kinds && filters.kinds.length > 0) q = q.in('kind', filters.kinds);
    const { error } = await q;
    if (error) throw error;
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('admin_notifications').delete().eq('id', id);
    if (error) throw error;
  }

  async deleteAllRead(filters?: Pick<NotificationFilters, 'kinds'>): Promise<void> {
    let q = anyFrom(this.supabase)('admin_notifications').delete().eq('is_read', true);
    if (filters?.kinds && filters.kinds.length > 0) q = q.in('kind', filters.kinds);
    const { error } = await q;
    if (error) throw error;
  }

  async ensureCheckDraftReminders(): Promise<number> {
    const client = this.supabase as unknown as { rpc: (n: string) => Promise<{ data: unknown; error: unknown }> };
    const { data, error } = await client.rpc('ensure_check_draft_reminders');
    if (error) throw error;
    return Number(data ?? 0);
  }

  async ensureLowStockNotifications(): Promise<number> {
    return ensureLowStockNotificationsForArticles(this.supabase);
  }
}

/** La RPC rend du camelCase ; `map` lit du snake_case. Un seul endroit traduit. */
function toRowShape(row: Record<string, unknown>): Record<string, unknown> {
  return {
    id: row['id'],
    kind: row['kind'],
    title: row['title'],
    message: row['message'],
    link_path: row['linkPath'],
    is_read: row['isRead'],
    read_at: row['readAt'],
    entity_type: row['entityType'],
    entity_id: row['entityId'],
    metadata: row['metadata'],
    created_at: row['createdAt'],
  };
}

function map(row: Record<string, unknown>): AdminNotification {
  return {
    id: row['id'] as string,
    kind: row['kind'] as NotificationKind,
    title: row['title'] as string,
    message: (row['message'] as string) ?? null,
    linkPath: (row['link_path'] as string) ?? null,
    isRead: (row['is_read'] as boolean) ?? false,
    readAt: (row['read_at'] as string) ?? null,
    entityType: (row['entity_type'] as string) ?? null,
    entityId: (row['entity_id'] as string) ?? null,
    metadata: (row['metadata'] as Record<string, unknown>) ?? {},
    createdAt: row['created_at'] as string,
  };
}

// ============================================================
// ABANDONED CARTS
// ============================================================

export class SupabaseAbandonedCartRepository implements IAbandonedCartRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: AbandonedCartFilters): Promise<AbandonedCart[]> {
    let q = anyFrom(this.supabase)('abandoned_carts').select('*');
    if (filters?.status) q = q.eq('status', filters.status);
    if (filters?.search) {
      q = q.or(orContainsAnyColumn(['phone', 'email', 'full_name'], filters.search));
    }
    const { data, error } = await q.order('last_activity_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapCart);
  }

  async getById(id: string): Promise<AbandonedCart | null> {
    const { data, error } = await anyFrom(this.supabase)('abandoned_carts')
      .select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapCart(data) : null;
  }

  async getByPhone(phone: string, tenantId?: string | null): Promise<AbandonedCart | null> {
    const normalized = phone.replace(/[^\d+]/g, '');
    let query = anyFrom(this.supabase)('abandoned_carts')
      .select('*').eq('phone', normalized).eq('status', 'active');
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return data ? mapCart(data) : null;
  }

  async upsertByPhone(input: UpsertAbandonedCartInput): Promise<AbandonedCart> {
    const normalized = (input.phone ?? '').replace(/[^\d+]/g, '');
    if (!normalized) throw new Error('phone_required');

    const rpcCart = await this.upsertByPhoneViaRpc(input, normalized);
    if (rpcCart) return rpcCart;

    const existing = await this.getByPhone(normalized, input.tenantId);
    const now = new Date().toISOString();
    if (existing) {
      const { data, error } = await anyFrom(this.supabase)('abandoned_carts')
        .update({
          email: input.email ?? existing.email,
          full_name: input.fullName ?? existing.fullName,
          city: input.city ?? existing.city,
          items: input.items,
          total_ttc: input.totalTtc,
          utm_source: input.utmSource ?? existing.utmSource,
          utm_campaign: input.utmCampaign ?? existing.utmCampaign,
          landing_page: input.landingPage ?? existing.landingPage,
          last_activity_at: now,
        })
        .eq('id', existing.id)
        .select().single();
      if (error) throw error;
      return mapCart(data);
    }

    const { data, error } = await anyFrom(this.supabase)('abandoned_carts')
      .insert({
        tenant_id: input.tenantId ?? undefined,
        phone: normalized,
        email: input.email ?? null,
        full_name: input.fullName ?? null,
        city: input.city ?? null,
        items: input.items,
        total_ttc: input.totalTtc,
        utm_source: input.utmSource ?? null,
        utm_campaign: input.utmCampaign ?? null,
        landing_page: input.landingPage ?? null,
        status: 'active',
        last_activity_at: now,
      })
      .select().single();
    if (error) throw error;
    return mapCart(data);
  }

  private async upsertByPhoneViaRpc(input: UpsertAbandonedCartInput, phone: string): Promise<AbandonedCart | null> {
    if (!input.tenantId || !input.cartToken) return null;
    const client = this.supabase as unknown as {
      rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    };
    const { data, error } = await client.rpc('upsert_storefront_abandoned_cart', {
      p_tenant_id: input.tenantId,
      p_cart_token: input.cartToken,
      p_phone: phone,
      p_email: input.email ?? null,
      p_full_name: input.fullName ?? null,
      p_city: input.city ?? null,
      p_items: input.items,
      p_total_ttc: input.totalTtc,
      p_utm_source: input.utmSource ?? null,
      p_utm_campaign: input.utmCampaign ?? null,
      p_landing_page: input.landingPage ?? null,
    });
    if (error) {
      if (isMissingRpc(error)) return null;
      throw normalizeSupabaseError(error);
    }
    return mapCart((data ?? {}) as Record<string, unknown>);
  }

  async update(id: string, input: UpdateAbandonedCartInput): Promise<AbandonedCart> {
    const patch: Record<string, unknown> = {};
    if (input.status !== undefined) patch['status'] = input.status;
    if (input.contactAttempts !== undefined) patch['contact_attempts'] = input.contactAttempts;
    if (input.lastContactedAt !== undefined) patch['last_contacted_at'] = input.lastContactedAt;
    if (input.notes !== undefined) patch['notes'] = input.notes;
    if (input.recoveredSalesDocumentId !== undefined) patch['recovered_sales_document_id'] = input.recoveredSalesDocumentId;
    const { data, error } = await anyFrom(this.supabase)('abandoned_carts')
      .update(patch).eq('id', id).select().single();
    if (error) throw error;
    return mapCart(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('abandoned_carts').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapCart(row: Record<string, unknown>): AbandonedCart {
  return {
    id: row['id'] as string,
    phone: (row['phone'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    fullName: (row['full_name'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    onlineCustomerId: (row['online_customer_id'] as string) ?? null,
    items: ((row['items'] as AbandonedCartItem[]) ?? []) as AbandonedCartItem[],
    totalTtc: Number(row['total_ttc'] ?? 0),
    utmSource: (row['utm_source'] as string) ?? null,
    utmCampaign: (row['utm_campaign'] as string) ?? null,
    landingPage: (row['landing_page'] as string) ?? null,
    status: (row['status'] as AbandonedCartStatus) ?? 'active',
    contactAttempts: (row['contact_attempts'] as number) ?? 0,
    lastContactedAt: (row['last_contacted_at'] as string) ?? null,
    recoveredSalesDocumentId: (row['recovered_sales_document_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lastActivityAt: row['last_activity_at'] as string,
  };
}

function isMissingRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as Record<string, unknown>;
  const code = String(record['code'] ?? '');
  const message = String(record['message'] ?? record['details'] ?? '');
  return code === 'PGRST202' || code === '42883' || /function .* does not exist|could not find the function/i.test(message);
}

function normalizeSupabaseError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] ?? record['details'] ?? record['hint'];
    if (typeof message === 'string' && message.trim()) return new Error(message);
  }
  return new Error(String(error));
}
